import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import statusPageApi from '../../services/api/status-page.api';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface StatusPageData {
  statusPage: any;
  components: any[];
  incidents: any[];
}

export default function PublicStatusPage() {
  const { slug } = useParams<{ slug: string }>();
  const { config: whiteLabelConfig } = useWhiteLabel();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StatusPageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadStatusPage();
      // Auto-refresh every 30 seconds
      const interval = setInterval(loadStatusPage, 30000);
      return () => clearInterval(interval);
    }
  }, [slug]);

  const loadStatusPage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load status page
      const pageResponse = await statusPageApi.getPublicStatusPage(slug!);
      if (!pageResponse.success) {
        setError('Status page not found');
        return;
      }

      const statusPage = pageResponse.data;

      // Load components
      const componentsResponse = await fetch(
        `${API_BASE_URL}/v1/status/${slug}/components`,
        { method: 'GET' }
      );
      const componentsData = await componentsResponse.json();

      // Load incidents (last 7 days)
      const incidentsResponse = await fetch(
        `${API_BASE_URL}/v1/status/${slug}/incidents`,
        { method: 'GET' }
      );
      const incidentsData = await incidentsResponse.json();

      setData({
        statusPage,
        components: componentsData.success ? componentsData.data : [],
        incidents: incidentsData.success ? incidentsData.data : [],
      });
    } catch (error: any) {
      console.error('Error loading status page:', error);
      setError('Failed to load status page');
    } finally {
      setLoading(false);
    }
  };

  const getOverallStatus = () => {
    if (!data || data.components.length === 0) {
      return { status: 'operational', message: 'All Systems Operational', color: 'green' };
    }

    const hasMajorOutage = data.components.some((c: any) => c.status === 'major_outage');
    const hasPartialOutage = data.components.some((c: any) => c.status === 'partial_outage');
    const hasDegraded = data.components.some((c: any) => c.status === 'degraded');
    const hasMaintenance = data.components.some((c: any) => c.status === 'maintenance');

    if (hasMajorOutage) {
      return { status: 'major_outage', message: 'Major System Outage', color: 'red' };
    }
    if (hasPartialOutage) {
      return { status: 'partial_outage', message: 'Partial System Outage', color: 'orange' };
    }
    if (hasDegraded) {
      return { status: 'degraded', message: 'Degraded Performance', color: 'yellow' };
    }
    if (hasMaintenance) {
      return { status: 'maintenance', message: 'Under Maintenance', color: 'blue' };
    }

    return { status: 'operational', message: 'All Systems Operational', color: 'green' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'text-green-600 dark:text-green-400';
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'partial_outage':
        return 'text-orange-600 dark:text-orange-400';
      case 'major_outage':
        return 'text-red-600 dark:text-red-400';
      case 'maintenance':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusDot = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-500';
      case 'degraded':
        return 'bg-yellow-500';
      case 'partial_outage':
        return 'bg-orange-500';
      case 'major_outage':
        return 'bg-red-500';
      case 'maintenance':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'major':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'minor':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group incidents by date
  const groupIncidentsByDate = () => {
    if (!data?.incidents) return {};

    const grouped: { [key: string]: any[] } = {};
    const today = new Date();

    // Add last 7 days
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      grouped[dateKey] = [];
    }

    data.incidents.forEach((incident: any) => {
      const dateKey = new Date(incident.started_at).toISOString().split('T')[0];
      if (grouped[dateKey]) {
        grouped[dateKey].push(incident);
      }
    });

    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading status page...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="mt-6 text-4xl font-bold text-gray-900 dark:text-white">404</h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">{error || 'Status page not found'}</p>
        </div>
      </div>
    );
  }

  const overallStatus = getOverallStatus();
  const groupedIncidents = groupIncidentsByDate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center space-x-4 mb-4">
            {data.statusPage.logo_url && (
              <img
                src={data.statusPage.logo_url}
                alt={data.statusPage.name}
                className="h-12 w-auto"
              />
            )}
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              {data.statusPage.name} Status
            </h1>
          </div>
          {data.statusPage.description && (
            <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
              {data.statusPage.description}
            </p>
          )}
        </div>
      </div>

      {/* Current Status Banner */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`rounded-xl p-8 border-2 ${
          overallStatus.color === 'green'
            ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-900/50'
            : overallStatus.color === 'yellow'
            ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-900/50'
            : overallStatus.color === 'orange'
            ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-900/50'
            : overallStatus.color === 'red'
            ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-900/50'
            : 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-900/50'
        }`}>
          <div className="flex items-center space-x-4">
            <div className={`h-4 w-4 rounded-full ${
              overallStatus.color === 'green' ? 'bg-green-500' :
              overallStatus.color === 'yellow' ? 'bg-yellow-500' :
              overallStatus.color === 'orange' ? 'bg-orange-500' :
              overallStatus.color === 'red' ? 'bg-red-500' : 'bg-blue-500'
            } animate-pulse`}></div>
            <div>
              <h2 className={`text-2xl font-bold ${
                overallStatus.color === 'green' ? 'text-green-900 dark:text-green-100' :
                overallStatus.color === 'yellow' ? 'text-yellow-900 dark:text-yellow-100' :
                overallStatus.color === 'orange' ? 'text-orange-900 dark:text-orange-100' :
                overallStatus.color === 'red' ? 'text-red-900 dark:text-red-100' : 'text-blue-900 dark:text-blue-100'
              }`}>
                {overallStatus.message}
              </h2>
              <p className={`mt-1 text-sm ${
                overallStatus.color === 'green' ? 'text-green-700 dark:text-green-300' :
                overallStatus.color === 'yellow' ? 'text-yellow-700 dark:text-yellow-300' :
                overallStatus.color === 'orange' ? 'text-orange-700 dark:text-orange-300' :
                overallStatus.color === 'red' ? 'text-red-700 dark:text-red-300' : 'text-blue-700 dark:text-blue-300'
              }`}>
                Last updated {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Active Incidents Alert */}
        {data.incidents.some((i: any) => i.status !== 'resolved') && (
          <div className="mt-6 rounded-xl bg-orange-50 border-2 border-orange-200 dark:bg-orange-900/10 dark:border-orange-900/50 p-6">
            <div className="flex items-start space-x-3">
              <svg className="h-6 w-6 text-orange-600 dark:text-orange-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="font-semibold text-orange-900 dark:text-orange-100">Active Incidents</h3>
                <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                  We are currently investigating {data.incidents.filter((i: any) => i.status !== 'resolved').length} incident(s).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Components Status */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            System Components
          </h3>
          {data.components.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="mt-4 text-gray-500 dark:text-gray-400">No components configured</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
              {data.components.map((component: any) => {
                const metadata = component.metadata ? (typeof component.metadata === 'string' ? JSON.parse(component.metadata) : component.metadata) : null;
                const healthCheck = metadata?.health_check;
                const isMonitored = healthCheck?.enabled === true;

                return (
                  <div key={component.id} className="px-6 py-5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`h-3 w-3 rounded-full ${getStatusDot(component.status)}`}></div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {component.name}
                          </h4>
                          {isMonitored && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Auto-monitored
                            </span>
                          )}
                        </div>
                        {component.description && (
                          <p className="mt-1 ml-6 text-sm text-gray-600 dark:text-gray-400">
                            {component.description}
                          </p>
                        )}
                        {isMonitored && healthCheck.url && (
                          <div className="mt-2 ml-6">
                            <div className="flex items-center space-x-4 text-sm">
                              <div className="flex items-center text-gray-600 dark:text-gray-400">
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                                  {healthCheck.url}
                                </code>
                              </div>
                              <div className="flex items-center text-gray-500 dark:text-gray-400">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs">Checked every {healthCheck.interval_seconds}s</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <span className={`ml-4 text-sm font-medium ${getStatusColor(component.status)}`}>
                        {getStatusLabel(component.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Incident History */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Incident History
          </h3>
          <div className="space-y-4">
            {Object.entries(groupedIncidents).map(([date, incidents]) => {
              const dateObj = new Date(date);
              const isToday = new Date().toDateString() === dateObj.toDateString();
              const dayIncidents = incidents as any[];

              return (
                <div key={date} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {isToday ? 'Today' : dateObj.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </h4>
                      {dayIncidents.length === 0 && (
                        <span className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          <span>No incidents</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {dayIncidents.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {dayIncidents.map((incident: any) => (
                        <div key={incident.id} className="px-6 py-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getSeverityColor(incident.severity)}`}>
                                  {incident.severity.toUpperCase()}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(incident.started_at)}
                                </span>
                              </div>
                              <h5 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                                {incident.title}
                              </h5>
                              {incident.message && (
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                  {incident.message}
                                </p>
                              )}
                              {/* Incident Updates */}
                              {incident.updates && incident.updates.length > 0 && (
                                <div className="mt-4 space-y-3 border-l-2 border-gray-300 dark:border-gray-600 pl-4">
                                  {incident.updates.map((update: any) => (
                                    <div key={update.id} className="text-sm">
                                      <div className="flex items-center space-x-2">
                                        <span className="font-semibold text-gray-900 dark:text-white capitalize">
                                          {update.status.replace(/_/g, ' ')}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {formatDate(update.created_at)}
                                        </span>
                                      </div>
                                      <p className="mt-1 text-gray-600 dark:text-gray-400">{update.message}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No incidents reported on this day.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Status Page powered by <span className="font-semibold">{whiteLabelConfig.custom_name || 'Kubmix Flux'}</span>
          </p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Auto-refreshes every 30 seconds
          </p>
        </div>
      </div>
    </div>
  );
}
